import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Input,
  Label,
  Separator,
  cn,
} from '@zafroi/ui-kit';
import {
  ArrowLeft,
  Code2,
  Eye,
  Info,
  Loader2,
  Monitor,
  Palette,
  PlusCircle,
  Save,
  Smartphone,
  Sparkles,
  Trash2,
  Upload,
} from 'lucide-react';
import {
  clearEmailBrandLogo,
  getEmailTemplates,
  previewEmailTemplate,
  updateEmailBrand,
  uploadEmailBrandLogo,
  upsertEmailTemplate,
} from '@/services/engage-api';
import { ApiError } from '@/services/api-client';
import type {
  EmailBrand,
  EmailTemplate,
  EmailTemplateMode,
  EmailTemplateType,
  PreviewEmailTemplateInput,
} from '@/types/engage';

type TabKey = EmailTemplateType | 'BRAND';

const TEMPLATE_TABS: {
  type: EmailTemplateType;
  label: string;
  hint: string;
}[] = [
  {
    type: 'AUTO_ACK',
    label: 'Auto-acknowledgement',
    hint: 'Sent automatically when a customer first emails your support address.',
  },
  {
    type: 'AGENT_REPLY',
    label: 'Agent reply',
    hint: "Wraps your team's manual replies so every message stays on-brand.",
  },
  {
    type: 'RESOLVED',
    label: 'Resolved notification',
    hint: 'Optional email sent when a request is marked resolved.',
  },
];

const TOKENS: { token: string; label: string; types: EmailTemplateType[] }[] = [
  { token: '{{customerName}}', label: 'Customer name', types: ['AUTO_ACK', 'AGENT_REPLY', 'RESOLVED'] },
  { token: '{{ticketNumber}}', label: 'Ticket reference', types: ['AUTO_ACK', 'AGENT_REPLY', 'RESOLVED'] },
  { token: '{{originalSubject}}', label: 'Original subject', types: ['AUTO_ACK', 'AGENT_REPLY', 'RESOLVED'] },
  { token: '{{tenantName}}', label: 'Your business name', types: ['AUTO_ACK', 'AGENT_REPLY', 'RESOLVED'] },
  { token: '{{message}}', label: "The agent's typed reply", types: ['AGENT_REPLY'] },
  { token: '{{resolutionNotes}}', label: 'Resolution notes', types: ['RESOLVED'] },
];

interface TemplateDraft {
  mode: EmailTemplateMode;
  enabled: boolean;
  subject: string;
  bodyCopy: string;
  customHtml: string;
}

function toDraft(t: EmailTemplate): TemplateDraft {
  return {
    mode: t.mode,
    enabled: t.enabled,
    subject: t.subject,
    bodyCopy: t.bodyCopy,
    customHtml: t.customHtml ?? '',
  };
}

const COLOR_FIELDS: { key: keyof EmailBrand; label: string }[] = [
  { key: 'brandColor', label: 'Brand colour' },
  { key: 'buttonColor', label: 'Button colour' },
  { key: 'pageColor', label: 'Page background' },
  { key: 'cardColor', label: 'Card background' },
];

export function EmailTemplatesPage() {
  const { channelId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['email-templates', channelId],
    queryFn: () => getEmailTemplates(channelId),
    enabled: !!channelId,
  });

  const [activeTab, setActiveTab] = useState<TabKey>('AUTO_ACK');
  const [drafts, setDrafts] = useState<Record<EmailTemplateType, TemplateDraft> | null>(null);
  const [brand, setBrand] = useState<EmailBrand & { companyName: string }>({ companyName: '' });
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  // Seed local editable state once the query resolves.
  useEffect(() => {
    if (!data) return;
    const map = {} as Record<EmailTemplateType, TemplateDraft>;
    for (const t of data.templates) map[t.type] = toDraft(t);
    // Guarantee all three exist even if the API omitted one.
    for (const tab of TEMPLATE_TABS) {
      if (!map[tab.type]) {
        map[tab.type] = {
          mode: 'THEMED',
          enabled: tab.type !== 'RESOLVED',
          subject: '',
          bodyCopy: '',
          customHtml: '',
        };
      }
    }
    setDrafts(map);
    setBrand(data.brand);
  }, [data]);

  const currentType: EmailTemplateType =
    activeTab === 'BRAND' ? 'AUTO_ACK' : activeTab;
  const currentDraft = drafts?.[currentType];

  // Debounced live preview whenever the active draft or brand changes.
  useEffect(() => {
    if (!drafts || !channelId) return;
    const draft = drafts[currentType];
    const input: PreviewEmailTemplateInput = {
      type: currentType,
      mode: draft.mode,
      subject: draft.subject,
      bodyCopy: draft.bodyCopy,
      customHtml: draft.customHtml,
      brand,
    };
    setPreviewing(true);
    const handle = setTimeout(() => {
      previewEmailTemplate(channelId, input)
        .then((r) => {
          setPreviewHtml(r.html);
          setPreviewSubject(r.subject);
        })
        .catch(() => {
          /* preview is best-effort */
        })
        .finally(() => setPreviewing(false));
    }, 450);
    return () => clearTimeout(handle);
  }, [drafts, brand, currentType, channelId]);

  const saveTemplate = useMutation({
    mutationFn: (type: EmailTemplateType) => {
      const d = drafts![type];
      return upsertEmailTemplate(channelId, type, {
        mode: d.mode,
        enabled: d.enabled,
        subject: d.subject,
        bodyCopy: d.bodyCopy,
        customHtml: d.mode === 'CUSTOM_HTML' ? d.customHtml : undefined,
      });
    },
    onSuccess: () => {
      toast.success('Template saved');
      queryClient.invalidateQueries({ queryKey: ['email-templates', channelId] });
    },
    onError: (err) =>
      toast.error('Could not save template', {
        description: err instanceof ApiError ? err.message : String(err),
      }),
  });

  const saveBrand = useMutation({
    mutationFn: () => updateEmailBrand(channelId, brand),
    onSuccess: (b) => {
      setBrand(b);
      toast.success('Brand saved');
      queryClient.invalidateQueries({ queryKey: ['email-templates', channelId] });
    },
    onError: (err) =>
      toast.error('Could not save brand', {
        description: err instanceof ApiError ? err.message : String(err),
      }),
  });

  function patchDraft(patch: Partial<TemplateDraft>) {
    setDrafts((prev) =>
      prev ? { ...prev, [currentType]: { ...prev[currentType], ...patch } } : prev,
    );
  }

  function insertToken(token: string) {
    if (activeTab === 'BRAND' || !currentDraft) return;
    if (currentDraft.mode === 'CUSTOM_HTML') {
      patchDraft({ customHtml: currentDraft.customHtml + token });
      return;
    }
    const el = bodyRef.current;
    if (!el) {
      patchDraft({ bodyCopy: currentDraft.bodyCopy + token });
      return;
    }
    const start = el.selectionStart ?? currentDraft.bodyCopy.length;
    const end = el.selectionEnd ?? currentDraft.bodyCopy.length;
    const next =
      currentDraft.bodyCopy.slice(0, start) + token + currentDraft.bodyCopy.slice(end);
    patchDraft({ bodyCopy: next });
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  }

  const relevantTokens = useMemo(
    () => TOKENS.filter((t) => t.types.includes(currentType)),
    [currentType],
  );

  if (isLoading || !drafts) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <p className="text-sm text-muted-foreground">
          Couldn't load this channel's email templates.
        </p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/channels')}>
          Back to channels
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/channels"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Channels
        </Link>
        <div className="mt-2 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Support email templates</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Design the branded emails customers receive. Edit the copy and brand;
              the preview on the right is exactly what gets sent.
            </p>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="mb-5 flex flex-wrap gap-1 border-b border-border">
        {TEMPLATE_TABS.map((t) => (
          <TabButton
            key={t.type}
            active={activeTab === t.type}
            onClick={() => setActiveTab(t.type)}
            label={t.label}
            disabled={drafts[t.type]?.enabled === false}
          />
        ))}
        <TabButton
          active={activeTab === 'BRAND'}
          onClick={() => setActiveTab('BRAND')}
          label="Brand"
          icon={<Palette className="h-3.5 w-3.5" />}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* ── Left: editor ── */}
        <div className="space-y-5">
          {activeTab === 'BRAND' ? (
            <BrandEditor channelId={channelId} brand={brand} setBrand={setBrand} />
          ) : (
            currentDraft && (
              <TemplateEditor
                type={currentType}
                draft={currentDraft}
                patch={patchDraft}
                bodyRef={bodyRef}
              />
            )
          )}

          {/* Token palette (templates only) */}
          {activeTab !== 'BRAND' && (
            <div className="rounded-md border border-border bg-muted/40 p-3">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Info className="h-3 w-3" />
                Click to insert a merge field at the cursor
              </p>
              <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {relevantTokens.map((p) => (
                  <li key={p.token} className="flex items-start gap-2">
                    <button
                      type="button"
                      onClick={() => insertToken(p.token)}
                      className="inline-flex shrink-0 items-center gap-1 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[11px] text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      <PlusCircle className="h-2.5 w-2.5" />
                      {p.token}
                    </button>
                    <span className="text-[11px] leading-relaxed text-muted-foreground">
                      {p.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Save bar */}
          <div className="flex items-center gap-3">
            {activeTab === 'BRAND' ? (
              <Button onClick={() => saveBrand.mutate()} disabled={saveBrand.isPending}>
                {saveBrand.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save brand
              </Button>
            ) : (
              <Button
                onClick={() => saveTemplate.mutate(currentType)}
                disabled={saveTemplate.isPending}
              >
                {saveTemplate.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save template
              </Button>
            )}
            <p className="text-xs text-muted-foreground">
              {activeTab === 'BRAND'
                ? 'Brand applies to all three email types.'
                : TEMPLATE_TABS.find((t) => t.type === currentType)?.hint}
            </p>
          </div>
        </div>

        {/* ── Right: live preview ── */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <div className="mb-2 flex items-center justify-between">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <Eye className="h-3 w-3" />
              Live preview
              {previewing && <Loader2 className="h-3 w-3 animate-spin" />}
            </p>
            <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
              <DeviceButton active={device === 'desktop'} onClick={() => setDevice('desktop')}>
                <Monitor className="h-3.5 w-3.5" />
              </DeviceButton>
              <DeviceButton active={device === 'mobile'} onClick={() => setDevice('mobile')}>
                <Smartphone className="h-3.5 w-3.5" />
              </DeviceButton>
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border border-border bg-muted/30 shadow-elev-1">
            <div className="border-b border-border bg-background px-4 py-2.5 text-xs">
              <span className="text-muted-foreground">Subject:&nbsp;</span>
              <span className="font-medium text-foreground">{previewSubject || '—'}</span>
            </div>
            <div className="flex justify-center p-4">
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={previewHtml}
                className={cn(
                  'h-[640px] w-full rounded border border-border bg-white transition-all',
                  device === 'mobile' ? 'max-w-[390px]' : 'max-w-[640px]',
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  label,
  icon,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors',
        active
          ? 'border-primary font-medium text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {icon}
      {label}
      {disabled && (
        <Badge tone="neutral" withDot={false} className="ml-1 text-[10px]">
          Off
        </Badge>
      )}
    </button>
  );
}

function DeviceButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded p-1 transition-colors',
        active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function TemplateEditor({
  type,
  draft,
  patch,
  bodyRef,
}: {
  type: EmailTemplateType;
  draft: TemplateDraft;
  patch: (p: Partial<TemplateDraft>) => void;
  bodyRef: React.RefObject<HTMLTextAreaElement>;
}) {
  return (
    <div className="space-y-4">
      {/* Enabled + mode toggles */}
      <div className="flex flex-wrap items-center gap-3">
        <ToggleChip
          on={draft.enabled}
          onClick={() => patch({ enabled: !draft.enabled })}
          label={draft.enabled ? 'Enabled' : 'Disabled'}
        />
        <div className="flex items-center gap-1 rounded-md border border-border p-0.5 text-xs">
          <ModeButton
            active={draft.mode === 'THEMED'}
            onClick={() => patch({ mode: 'THEMED' })}
            icon={<Sparkles className="h-3.5 w-3.5" />}
            label="Themed"
          />
          <ModeButton
            active={draft.mode === 'CUSTOM_HTML'}
            onClick={() => patch({ mode: 'CUSTOM_HTML' })}
            icon={<Code2 className="h-3.5 w-3.5" />}
            label="Custom HTML"
          />
        </div>
        {type === 'RESOLVED' && !draft.enabled && (
          <span className="text-[11px] text-muted-foreground">
            Off by default — enable to email customers on resolve.
          </span>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="tpl-subject" className="text-xs">
          Subject
        </Label>
        <Input
          id="tpl-subject"
          value={draft.subject}
          onChange={(e) => patch({ subject: e.target.value })}
          className="font-mono text-xs"
        />
      </div>

      {draft.mode === 'THEMED' ? (
        <div className="space-y-2">
          <Label htmlFor="tpl-body" className="text-xs">
            Message
          </Label>
          <textarea
            id="tpl-body"
            ref={bodyRef}
            value={draft.bodyCopy}
            onChange={(e) => patch({ bodyCopy: e.target.value })}
            rows={14}
            className="min-h-72 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-xs leading-relaxed shadow-elev-1 focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="text-[11px] text-muted-foreground">
            Plain text — we wrap it in your branded shell automatically. Blank lines
            start new paragraphs.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="tpl-html" className="text-xs">
            Custom HTML
          </Label>
          <textarea
            id="tpl-html"
            value={draft.customHtml}
            onChange={(e) => patch({ customHtml: e.target.value })}
            rows={18}
            spellCheck={false}
            className="min-h-96 w-full resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-[11px] leading-relaxed shadow-elev-1 focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="text-[11px] text-muted-foreground">
            Full HTML document. Merge fields still work; the branded shell is not
            applied in this mode.
          </p>
        </div>
      )}
    </div>
  );
}

const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml';
const LOGO_ALLOWED = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']);
const LOGO_MAX_BYTES = 5 * 1024 * 1024;

function BrandEditor({
  channelId,
  brand,
  setBrand,
}: {
  channelId: string;
  brand: EmailBrand & { companyName: string };
  setBrand: React.Dispatch<React.SetStateAction<EmailBrand & { companyName: string }>>;
}) {
  function set<K extends keyof EmailBrand>(key: K, value: EmailBrand[K]) {
    setBrand((b) => ({ ...b, [key]: value }));
  }
  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        These apply to every support email. Defaults are seeded from your
        organisation logo + chatbot colour; override anything here.
      </p>

      <div className="space-y-2">
        <Label htmlFor="brand-content-name" className="text-xs">
          Business name — in the email body
        </Label>
        <Input
          id="brand-content-name"
          value={brand.contentName ?? ''}
          placeholder="e.g. MAVK"
          onChange={(e) => set('contentName', e.target.value)}
        />
        <p className="text-[11px] text-muted-foreground">
          Used wherever <code className="rounded bg-muted px-1 py-0.5">{'{{tenantName}}'}</code> appears
          in the message (e.g. the sign-off).
        </p>
      </div>

      <LogoUpload
        channelId={channelId}
        value={brand.logoUrl ?? null}
        onChange={(b) => setBrand((prev) => ({ ...prev, ...b }))}
      />

      <Separator />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {COLOR_FIELDS.map((f) => (
          <ColorField
            key={f.key as string}
            label={f.label}
            value={(brand[f.key] as string | null | undefined) ?? ''}
            onChange={(v) => set(f.key, v as EmailBrand[typeof f.key])}
          />
        ))}
      </div>

      <Separator />

      {/* Footer area — grouped together */}
      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Footer
        </div>
        <div className="space-y-2">
          <Label htmlFor="brand-company" className="text-xs">
            Company name — in the footer (© line)
          </Label>
          <Input
            id="brand-company"
            value={brand.companyName ?? ''}
            placeholder="e.g. MAVK House Pvt. Ltd."
            onChange={(e) => set('companyName', e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="brand-footer" className="text-xs">
            Footer note (optional)
          </Label>
          <Input
            id="brand-footer"
            placeholder="e.g. You're receiving this because you contacted our support team."
            value={brand.footerText ?? ''}
            onChange={(e) => set('footerText', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

/** Drag-drop / click logo uploader with preview + size guidance. */
function LogoUpload({
  channelId,
  value,
  onChange,
}: {
  channelId: string;
  value: string | null;
  onChange: (brand: EmailBrand & { companyName: string }) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const upload = useMutation({
    mutationFn: (file: File) => uploadEmailBrandLogo(channelId, file),
    onSuccess: (b) => {
      onChange(b);
      toast.success('Logo uploaded');
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Upload failed'),
  });
  const remove = useMutation({
    mutationFn: () => clearEmailBrandLogo(channelId),
    onSuccess: (b) => {
      onChange(b);
      toast.success('Logo removed');
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Could not remove'),
  });

  const busy = upload.isPending || remove.isPending;
  const pick = (file: File | undefined | null) => {
    if (!file) return;
    if (!LOGO_ALLOWED.has(file.type)) {
      toast.error('Use a PNG, JPG, WebP or SVG image.');
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast.error('Logo must be under 5 MB.');
      return;
    }
    upload.mutate(file);
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">Logo</Label>
      <div
        className={cn(
          'flex items-center gap-3 rounded-md border border-dashed p-3 transition-colors',
          dragOver ? 'border-primary bg-primary/5' : 'border-input',
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          pick(e.dataTransfer.files?.[0]);
        }}
      >
        <div className="grid h-14 w-24 shrink-0 place-items-center overflow-hidden rounded border border-border bg-muted">
          {busy ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : value ? (
            <img src={value} alt="Logo preview" className="h-full w-full object-contain" />
          ) : (
            <span className="text-[10px] text-muted-foreground">none</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()}>
              <Upload className="h-3.5 w-3.5" />
              {value ? 'Replace' : 'Upload logo'}
            </Button>
            {value && (
              <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => remove.mutate()}>
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </Button>
            )}
          </div>
          <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
            Drag &amp; drop or click. PNG, JPG, WebP or SVG · up to 5&nbsp;MB ·
            <strong> ~240×88px recommended</strong> (wide wordmark). Transparent PNG looks best.
            Leave empty to show your business name as text.
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={LOGO_ACCEPT}
          className="hidden"
          onChange={(e) => {
            pick(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const safe = /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#ffffff';
  return (
    <div className="space-y-2">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={safe}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-10 cursor-pointer rounded border border-input bg-background"
        />
        <Input
          value={value}
          placeholder="#000000"
          onChange={(e) => onChange(e.target.value)}
          className="font-mono text-xs"
        />
      </div>
    </div>
  );
}

function ToggleChip({
  on,
  onClick,
  label,
}: {
  on: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        on
          ? 'border-primary/30 bg-primary/10 text-primary'
          : 'border-border bg-muted text-muted-foreground',
      )}
    >
      <span
        className={cn(
          'h-2 w-2 rounded-full',
          on ? 'bg-primary' : 'bg-muted-foreground/50',
        )}
      />
      {label}
    </button>
  );
}

function ModeButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded px-2.5 py-1 transition-colors',
        active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
